/**
 * Shared geo helpers for the globe renderer: the US state name->code map and
 * lon/lat -> 3D cartesian conversion. Extracted verbatim from globe-viewer.ts
 * (Phase 2, PR-C method split) so the scene/layers/interactions mixins can
 * share them. Typed and type-checked (no @ts-nocheck) — this module is imported
 * by the build-time geometry precompute under the strict root tsconfig.
 */
/* eslint-disable */

    // Radius of the region fill caps. Slightly below borders (1.005) and above
    // the globe (1.0). Shared by the runtime fill loader and the build-time
    // geometry precompute (scripts/generate-region-render.ts).
    export const FILL_RADIUS = 1.004

    // Radius of the border lines. Sits just above the fill caps (1.004) so
    // outlines read on top of fills. Shared by the build-time precompute.
    export const BORDER_RADIUS = 1.005

    // Curvature tessellation resolution in angular degrees, shared by the conic
    // fill caps and border densification so fills and borders agree. Lower =
    // smoother, at higher triangle cost. Lives here (lib-free) so the client can
    // read it without importing the conic geometry library.
    export const GLOBE_CURVATURE_RESOLUTION = 5

    // State name to code mapping
    export const STATE_NAME_TO_CODE: Record<string, string> = {
      'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR',
      'California': 'CA', 'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE',
      'District of Columbia': 'DC', 'Florida': 'FL', 'Georgia': 'GA', 'Hawaii': 'HI',
      'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
      'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME',
      'Maryland': 'MD', 'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN',
      'Mississippi': 'MS', 'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE',
      'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM',
      'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
      'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI',
      'South Carolina': 'SC', 'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX',
      'Utah': 'UT', 'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA',
      'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
    };

    // Convert geographic coordinates to 3D cartesian
    export function geoToCartesian(lon: number, lat: number, radius = 1): number[] {
      const lambda = (lon * Math.PI) / 180;
      const phi = (lat * Math.PI) / 180;

      const x = radius * Math.cos(phi) * Math.cos(lambda);
      const y = radius * Math.sin(phi);
      const z = -radius * Math.cos(phi) * Math.sin(lambda);

      return [x, y, z];
    }

    // Insert linearly-interpolated lon/lat points so no edge between consecutive
    // points spans more than `maxDegrees`. Border lines then follow the sphere
    // curvature instead of chording across it. Note this is a linear lon/lat
    // split keyed off the larger of |dLon|/|dLat|; the conic fill caps densify
    // their contours along great-circle arcs (d3-geo geoInterpolate, keyed off
    // great-circle distance). At this resolution the two are visually close but
    // not identical, so borders approximate — rather than exactly trace — the
    // fill edges. Source rings are split at the antimeridian, so no edge wraps
    // 360deg.
    export function densifyRingDegrees(ring: number[][], maxDegrees: number): number[][] {
      if (!ring || ring.length < 2 || !(maxDegrees > 0)) return ring;

      const out: number[][] = [];
      for (let i = 0; i < ring.length - 1; i++) {
        const [lon1, lat1] = ring[i];
        const [lon2, lat2] = ring[i + 1];
        out.push([lon1, lat1]);

        const dLon = lon2 - lon1;
        const dLat = lat2 - lat1;
        const span = Math.max(Math.abs(dLon), Math.abs(dLat));
        const steps = Math.floor(span / maxDegrees);
        for (let s = 1; s <= steps; s++) {
          const t = s / (steps + 1);
          out.push([lon1 + dLon * t, lat1 + dLat * t]);
        }
      }
      out.push(ring[ring.length - 1]);
      return out;
    }

    // Bounding-box center [lon, lat] of a GeoJSON geometry. Used as the region's
    // centroid for camera centering. Precomputed into the render bundle so the
    // client doesn't need raw coordinates at runtime.
    interface GeoGeometry {
      type: string
      coordinates: unknown
    }

    export function boundingBoxCentroid(geometry: GeoGeometry): number[] {
      let minLon = Infinity, maxLon = -Infinity;
      let minLat = Infinity, maxLat = -Infinity;

      const processCoords = (coords: any) => {
        if (typeof coords[0] === 'number') {
          const [lon, lat] = coords;
          if (lon < minLon) minLon = lon;
          if (lon > maxLon) maxLon = lon;
          if (lat < minLat) minLat = lat;
          if (lat > maxLat) maxLat = lat;
        } else {
          coords.forEach((c: any) => processCoords(c));
        }
      };

      processCoords(geometry.coordinates);
      return [(minLon + maxLon) / 2, (minLat + maxLat) / 2];
    }

    // Flat xyz position pairs (for LineSegmentsGeometry) tracing a feature's
    // polygon rings at `radius`, densified so segments follow the sphere
    // curvature. Precomputed into the render bundle; the client just uploads the
    // result. Mirrors the legacy runtime border builder exactly.
    export function buildBorderPositions(geometry: GeoGeometry, radius: number): number[] {
      const positions: number[] = [];

      const processCoords = (coords: any) => {
        const ring = Array.isArray(coords[0][0]) ? coords : [coords];
        ring.forEach((rawRing: any) => {
          const r = densifyRingDegrees(rawRing, GLOBE_CURVATURE_RESOLUTION);
          for (let i = 0; i < r.length - 1; i++) {
            const [lon1, lat1] = r[i];
            const [lon2, lat2] = r[i + 1];
            const [x1, y1, z1] = geoToCartesian(lon1, lat1, radius);
            const [x2, y2, z2] = geoToCartesian(lon2, lat2, radius);
            positions.push(x1, y1, z1, x2, y2, z2);
          }
        });
      };

      if (geometry.type === 'Polygon') {
        processCoords(geometry.coordinates);
      } else if (geometry.type === 'MultiPolygon') {
        (geometry.coordinates as any[]).forEach((polygon) => processCoords(polygon));
      }

      return positions;
    }
